import { cn } from "@/lib/utils";
import { formatDuration } from "@/lib/format-utils";
import {
  useAdminKpiSummary,
  type KpiMetric,
  type KpiSummary,
} from "@/hooks/queries/use-admin-activity";

interface KpiSummaryCardsProps {
  startDate: string;
  endDate: string;
  userIds?: string[];
}

interface CardDef {
  key: keyof KpiSummary;
  label: string;
  format: "count" | "duration" | "decimal";
}

const CARDS: CardDef[] = [
  { key: "activeUsers", label: "Active Users", format: "count" },
  { key: "totalActions", label: "Total Actions", format: "count" },
  { key: "totalTimeInvestedMinutes", label: "Time Invested", format: "duration" },
  { key: "avgActivityPerUser", label: "Avg Activity / User", format: "decimal" },
  { key: "aiSessionMinutes", label: "AI Session Time", format: "duration" },
];

function formatValue(value: number, format: "count" | "duration" | "decimal"): string {
  if (format === "duration") return formatDuration(value);
  if (format === "decimal") return value.toFixed(1);
  return value.toLocaleString();
}

function DeltaBadge({ metric }: { metric: KpiMetric }) {
  const { current, previous, deltaPercent } = metric;

  if (deltaPercent === null) {
    return (
      <span className="text-xs font-medium text-muted-foreground">
        {current === 0 ? "—" : previous === 0 ? "New" : "—"}
      </span>
    );
  }

  if (deltaPercent === 0) {
    return <span className="text-xs font-medium text-muted-foreground">—</span>;
  }

  const isPositive = deltaPercent > 0;

  return (
    <span
      className={cn(
        "text-xs font-medium",
        isPositive ? "text-emerald-400" : "text-red-400"
      )}
    >
      {isPositive ? "↑" : "↓"}
      {Math.abs(deltaPercent).toFixed(1)}%
    </span>
  );
}

function SkeletonCards() {
  return (
    <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
      {Array.from({ length: 5 }).map((_, i) => (
        <div
          key={i}
          className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4"
        >
          <div className="h-3 w-24 animate-pulse bg-muted rounded mb-3" />
          <div className="h-7 w-16 animate-pulse bg-muted rounded mb-2" />
          <div className="h-3 w-12 animate-pulse bg-muted rounded" />
        </div>
      ))}
    </div>
  );
}

export function KpiSummaryCards({ startDate, endDate, userIds }: KpiSummaryCardsProps) {
  const { data, isLoading, isError } = useAdminKpiSummary(startDate, endDate, userIds);

  if (isLoading) return <SkeletonCards />;

  if (isError) {
    return (
      <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-4 py-2 text-sm text-destructive">
        Failed to load KPI summary.
      </div>
    );
  }

  if (!data) return null;

  return (
    <div className="grid gap-3 grid-cols-[repeat(auto-fit,minmax(180px,1fr))]">
      {CARDS.map((card) => {
        const metric = data[card.key];
        return (
          <div
            key={card.key}
            className="bg-white/[0.03] border border-white/[0.06] rounded-lg p-4"
          >
            <p className="text-xs font-medium text-muted-foreground mb-1">
              {card.label}
            </p>
            <p className="text-2xl font-semibold tabular-nums leading-tight mb-1">
              {formatValue(metric.current, card.format)}
            </p>
            <DeltaBadge metric={metric} />
          </div>
        );
      })}
    </div>
  );
}
